import {
  DataGridPremium,
  DataGridPremiumProps,
  gridClasses,
  GridColumnOrderChangeParams,
  MuiEvent,
  GridCallbackDetails,
  GridApiPremium,
} from '@mui/x-data-grid-premium';
import type { CiqGridValidRowModel } from 'muigrid/src/types/types';
import { MutableRefObject, useMemo } from 'react';

import { colors, QBox, SxProps, Theme, ThemeProvider, useTheme } from 'quotient/vNext';

import type { VisualizationColumn } from './types/mui-table';
import { configureTableColumn } from './utils/columns';

// Exported type for MUI DataGrid pivot-related props
export type MuiDataGridPivotProps = Partial<
  Pick<
    DataGridPremiumProps,
    | 'disablePivoting'
    | 'pivotModel'
    | 'pivotActive'
    | 'pivotPanelOpen'
    | 'onPivotModelChange'
    | 'onPivotActiveChange'
    | 'onPivotPanelOpenChange'
  >
>;

const NoRowsOverlay = () => {
  return (
    <div
      style={{
        padding: '20px',
        textAlign: 'center',
      }}
    >
      No data available
    </div>
  );
};

const TABLE_STYLES: SxProps<Theme> = {
  height: '100%',
  [`.${gridClasses.root}`]: {
    border: 0,
    borderRadius: 0,
    borderColor: colors.neutral.primary['400'],
    '--DataGrid-rowBorderColor': colors.neutral.primary['400'],
    [`.${gridClasses.row}:hover`]: {
      backgroundColor: colors.neutral.secondary['50'],
    },
    [`.${gridClasses.columnHeader}, .${gridClasses.filler}`]: {
      borderBottomColor: colors.neutral.primary['700'],
    },
    [`.${gridClasses.columnHeaderTitleContainerContent}`]: {
      width: '100%',
    },
    [`.${gridClasses.columnHeaderTitle}`]: {
      fontWeight: 'bold',
      width: '100%',
      textAlign: 'center', // Default center alignment
    },
    [`.${gridClasses.columnSeparator}`]: {
      display: 'none',
    },
    [`.${gridClasses.cell}`]: {
      fontSize: '14px',
      padding: '16px 12px',
    },
    [`.${gridClasses.cell}.actions-cell`]: {
      backgroundColor: 'transparent !important',
      overflow: 'visible !important',
    },
    [`.${gridClasses.cell}.actions-cell button`]: {
      backgroundColor: 'transparent !important',
    },
    [`.${gridClasses.cell}.actions-cell button:hover`]: {
      backgroundColor: 'transparent !important',
    },
    [`.${gridClasses.cell}.actions-cell button:active`]: {
      backgroundColor: 'transparent !important',
    },
    [`.${gridClasses.cell}.actions-cell button:focus`]: {
      backgroundColor: 'transparent !important',
    },
    [`.${gridClasses.filler} div`]: {
      borderTopColor: 'transparent',
    },
    [`.${gridClasses.pivotPanelHeader}`]: {
      paddingLeft: '8px',
      height: '44px',
    },
    [`.${gridClasses.pivotPanelSearchContainer}`]: {
      padding: '4px 6px',
    },
    [`.${gridClasses.pivotPanelSearchContainer} .MuiInputBase-root`]: {
      margin: '0',
    },
    [`.${gridClasses.pivotPanelSwitchLabel}`]: {
      marginLeft: '8px',
    },
    [`.${gridClasses.pivotPanelField}`]: {
      paddingLeft: '14px',
    },
    [`.${gridClasses.sidebar}`]: {
      borderTop: `1px solid ${colors.neutral.primary['400']}`,
      borderRight: `1px solid ${colors.neutral.primary['400']}`,
      borderBottom: `1px solid ${colors.neutral.primary['400']}`,
      borderRadius: '4px',
    },
    // Generic column header alignment based on MUI column types
    [`.${gridClasses.columnHeader}--numeric .${gridClasses.columnHeaderTitle}`]: {
      textAlign: 'right', // Right align numeric columns
    },
    [`.${gridClasses.columnHeader}--string .${gridClasses.columnHeaderTitle}`]: {
      textAlign: 'left', // Left align string columns
    },
    [`.${gridClasses.columnHeader}--date .${gridClasses.columnHeaderTitle}`]: {
      textAlign: 'left', // Left align date columns
    },

    // Target aggregation column headers specifically
    [`.MuiDataGrid-aggregationColumnHeader .${gridClasses.columnHeaderTitle}`]: {
      textAlign: 'right',
      alignItems: 'flex-end',
      justifyContent: 'flex-end',
    },
    [`.${gridClasses.columnHeader}--numeric .MuiDataGrid-columnHeaderTitleContainerContent`]: {
      width: '100%',
      textAlign: 'right',
      alignItems: 'flex-end',
      justifyContent: 'flex-end',
      display: 'flex',
    },
    // Ensure toolbar is visible for pivot functionality
    [`.${gridClasses.toolbarContainer}`]: {
      display: 'flex',
      backgroundColor: colors.neutral.primary['50'],
      borderBottom: `1px solid ${colors.neutral.primary['400']}`,
      minHeight: '48px',
    },
  },
};

const AUTO_ROW_HEIGHT = (): 'auto' => 'auto';

export type MuiTableProps = {
  columns: VisualizationColumn[];
  controlledDataGridRef: MutableRefObject<GridApiPremium>;
  isLoading?: boolean;
  records: CiqGridValidRowModel[];
  pivotProps?: MuiDataGridPivotProps;
  disableColumnReorder?: boolean;
  onColumnOrderChange?: (
    params: GridColumnOrderChangeParams,
    event: MuiEvent<{}>,
    details: GridCallbackDetails,
  ) => void;
};

export function MuiTable({
  columns: inputColumns,
  controlledDataGridRef,
  isLoading,
  records,
  disableColumnReorder = true,
  pivotProps = { disablePivoting: false },
  onColumnOrderChange = undefined,
}: MuiTableProps) {
  const columns = useMemo(() => inputColumns.map((col) => configureTableColumn(col)), [inputColumns]);
  const theme = useTheme();

  const slotProps: DataGridPremiumProps['slotProps'] = useMemo(
    () => ({
      loadingOverlay: {
        variant: 'linear-progress',
        noRowsVariant: 'linear-progress',
      },
      toolbar: {
        printOptions: { disableToolbarButton: true },
      },
    }),
    [],
  );

  return (
    <ThemeProvider theme={theme}>
      <QBox data-testid="mui-table" sx={TABLE_STYLES}>
        <DataGridPremium
          apiRef={controlledDataGridRef}
          autosizeOptions={{
            includeHeaders: true,
          }}
          cellSelection
          checkboxSelection={false}
          columns={columns}
          density="compact"
          disableClipboardPaste
          disableColumnFilter={pivotProps?.disablePivoting !== false}
          disableColumnMenu={pivotProps?.disablePivoting !== false}
          disableColumnPinning={pivotProps?.disablePivoting !== false}
          disableColumnReorder={disableColumnReorder}
          disableColumnSelector={pivotProps?.disablePivoting !== false}
          disableMultipleColumnsSorting
          disableRowSelectionOnClick
          hideFooter={pivotProps?.disablePivoting !== false}
          getRowHeight={AUTO_ROW_HEIGHT}
          ignoreValueFormatterDuringExport
          loading={isLoading}
          rows={records}
          slotProps={slotProps}
          showToolbar={pivotProps?.disablePivoting !== false}
          onColumnOrderChange={onColumnOrderChange}
          {...pivotProps}
        />
      </QBox>
    </ThemeProvider>
  );
}
